import React from 'react';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import { licensedStatesShort } from '../constants/marketExpansion';

// Terms of Service for Go Doorly, LLC. Static informational page — public
// route, no auth. Mirrors the structure of Privacy.js.
//
// ⚠️ DRAFTED FROM WHAT THE SYSTEM ACTUALLY DOES, not from a template. Every
// factual claim below is traceable to code:
//   · purchase-and-resale structure  → knowledge/docs/doorly-process.md
//   · where Doorly operates          → constants/marketExpansion.js LICENSED_STATES (Texas only, Jesse 2026-10-29)
//   · fee non-refundable + credit    → constants/doorlyProgramTerms.js
//   · Eva's limits                   → knowledge/index.js allowlist + gates
//   · deletion behaviour             → routes/users.js DELETE /account
//
// ⚠️ AND SEVEN SECTIONS ARE DELIBERATELY ABSENT: dispute resolution/arbitration,
// governing law and venue, limitation of liability, disclaimer of warranties,
// intellectual property, minimum age, and changes-to-terms notice. Nothing in
// the codebase describes any of them, so drafting them here would be inventing
// obligations rather than documenting them — the same failure as a privacy
// policy naming a processor the company does not use. They are omitted rather
// than stubbed, so their absence is visible instead of papered over.
export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      {/* 🔑 The landing page's header and menu (Jesse, 2026-10-19): it sits in the flow and pads for the safe area,
          so the top of the page is never under it, on the web or in the app. */}
      <LandingNav />
      <main className="max-w-3xl mx-auto px-6 pt-10 pb-12">
        <h1 className="text-3xl font-bold text-black mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-8">
          Go Doorly, LLC · NMLS #2764749 · Effective August 13, 2026
        </p>

        {/* ⚠️ WHERE A SIGNED DOCUMENT DISAGREES, THE SIGNED DOCUMENT WINS. Kept
            as substance rather than as a review banner: borrowers sign a PSA and
            program terms whose clauses are more specific than anything here. */}
        <p className="text-sm text-gray-600 mb-8 leading-relaxed">
          Where these terms conflict with a document you have signed — including your purchase
          agreement or program terms — <strong>the signed document controls</strong>.
        </p>

        <section className="space-y-8 text-sm text-gray-700 leading-relaxed">
          <div>
            <h2 className="text-lg font-bold text-black mb-2">1. What Doorly Is</h2>
            <p className="font-semibold text-black mb-2">
              Doorly does not lend you money to buy a home from someone else. Doorly buys the home
              and sells it to you.
            </p>
            <p>
              When your application is approved and you choose a property, Doorly purchases that
              property from the seller in cash. Doorly then sells it to you and provides the
              financing. You become the homeowner on the day of closing.
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1">
              <li><strong>Doorly is the seller of your home</strong>, not a third party to a transaction between you and someone else.</li>
              <li><strong>Any existing purchase contract on the property must be terminated</strong> before Doorly can submit its offer, because Doorly must be the buyer.</li>
              <li>Doorly writes and submits the offer, negotiates with the listing side, and coordinates the transaction.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">2. Eligibility</h2>
            <p>
              Doorly currently operates only in <strong>{licensedStatesShort()}</strong>. Properties
              outside {licensedStatesShort()} are not eligible.
            </p>
            <p className="mt-3">
              Approval depends on criteria including credit history, income, debt obligations and
              the property itself. Meeting any single criterion does not mean you will be approved.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">3. The Doorly Assistant ("Eva")</h2>
            <p className="font-semibold text-black mb-2">
              Eva is an automated assistant, not a person and not an adviser.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Eva provides <strong>general information</strong> about how the Doorly program works. She does not give financial, legal or tax advice.</li>
              <li><strong>Nothing Eva says is a commitment, an approval, a decision, or an offer of credit.</strong> Only a written decision from the Doorly team is any of those things.</li>
              <li>Eva cannot approve, decline, price or expedite your application.</li>
              <li>If Eva tells you something that conflicts with a written communication from the Doorly team or with a document you have signed, <strong>the written communication or signed document controls</strong>.</li>
              <li>Eva's replies are generated automatically and may be incomplete or wrong. If something matters to your decision, ask a person.</li>
            </ul>
            <p className="mt-3">
              Your conversations with Eva are processed by a third-party AI provider and retained as
              part of your file. See our{' '}
              <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>.
            </p>
          </div>

          <div>
            {/* ── 🚨 THIS SECTION SAID THE OPPOSITE OF PROGRAM AGREEMENT §12 ───
                It read: "This fee is non-refundable", "It is not a deposit",
                and then listed as NO-refund cases the exact three cases §12 now
                promises a FULL REFUND for — offer not accepted, home
                disqualified, borrower decides not to proceed. It also called it
                a lender credit, which the LE and CD have not disclosed it as
                for some time (it renders on the Deposit line, 1026.37(h)(1)(iii)
                / 1026.38(j)(2)(ii)).

                A public terms page contradicting the agreement a borrower signs
                is worse than either document alone being wrong, because
                whichever one they read second looks like the mistake. Rewritten
                to §12 as approved 2026-10-05.

                ⚠️ STILL A DRAFT PENDING COUNSEL REVIEW — see the route comment
                in App.js. This change aligns it with §12; it does not make
                either reviewed. */}
            <h2 className="text-lg font-bold text-black mb-2">4. Fees and deposits</h2>
            <p>
              <strong>Purchase Initiation Deposit.</strong> When you ask Doorly to start buying a
              home for you, you pay a Purchase Initiation Deposit.{' '}
              <strong>The amount is shown to you before you pay it</strong>, and appears again on
              the Deposit line of your Loan Estimate and Closing Disclosure. It is collected by ACH
              bank transfer at the time you submit.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 my-3">
              <p className="font-bold text-black mb-2">
                This is a deposit toward your purchase — not a fee, and not a charge for credit.
              </p>
              <p>Doorly holds it in a separate account. You can get it back:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>
                  If the seller <strong>does not accept</strong> Doorly&rsquo;s offer, or the home{' '}
                  <strong>does not qualify</strong>, you may apply your deposit to another home or
                  request a <strong>full refund</strong>.
                </li>
                <li>
                  If you decide <strong>not to continue</strong> before you sign a purchase
                  agreement with Doorly, you may request a <strong>full refund</strong>.
                </li>
              </ul>
              <p className="mt-3">
                Once you sign a purchase agreement with Doorly, your deposit is governed by that
                agreement. It is <strong>credited in full toward your purchase at closing</strong>,
                and may be forfeited only if you fail to complete the purchase as that agreement
                provides.
              </p>
              <p className="mt-3 text-sm text-gray-600">
                This mirrors section 12 of your Program Agreement. If the two ever differ, your
                signed Program Agreement controls.
              </p>
            </div>
            <p>
              <strong>Showing fees.</strong> Three property showings are included. Each additional
              showing is charged at the time of scheduling, is non-refundable, and is not credited
              toward closing costs.
            </p>
            <p className="mt-3">
              <strong>Other fees.</strong> Program fees and closing costs are disclosed in your Loan
              Estimate and Closing Disclosure. Those documents control.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">5. Your Account, and Ending It</h2>
            <p>
              You may delete your account at any time from <strong>Settings → Delete Account</strong>,
              or by contacting us.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 my-3">
              <p className="font-bold text-black mb-2">What deletion does and does not do.</p>
              <p>
                Deleting your account removes your name and email from your profile and signs you
                out permanently. <strong>It does not delete your application file.</strong> Records
                of your application, the documents you uploaded, and any decision made on it are
                retained — federal law requires us to keep them, and for at least 25 months after a
                decision (Regulation B, 12 C.F.R. § 1002.12). See our{' '}
                <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>.
              </p>
            </div>
            <p>
              <strong>We may suspend or close an account</strong> that is used to submit false
              information, to attempt to access another person's file, or in a way that disrupts the
              platform. Where an application is already in progress, we will tell you what happens
              to it.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">6. What We Do Not Promise</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Approval.</strong> Submitting an application, uploading documents, or paying your Purchase Initiation Deposit does not approve you.</li>
              <li><strong>That an offer will be accepted.</strong> Doorly submits offers; sellers accept or reject them.</li>
              <li><strong>A timeframe.</strong> Estimated timelines are estimates.</li>
              <li><strong>Availability.</strong> The platform may be unavailable for maintenance or for reasons outside our control.</li>
            </ul>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Go Doorly, LLC · NMLS #2764749 · Equal Housing Lender. Questions? Contact us or visit{' '}
              <a href="https://help.godoorly.com" className="text-blue-600 underline">help.godoorly.com</a>.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
